import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subscription, finalize, forkJoin, of } from 'rxjs';
import { MyOskLectureGroupsService } from '../../../api/api-services/my-osk-lecture-groups.service';
import { MyOskStudentsService } from '../../../api/api-services/my-osk-students.service';
import { centerDynamicModalConfig } from '../../shared/helpers/modal.config';
import { InternalExamDialogComponent } from '../students/internal-exam/internal-exam-dialog.component';
import { InternalExamSessionMode } from '../students/internal-exam/internal-exam.models';
import { LECTURE_GROUP_STUDENTS_PAGE_SIZE } from '../lecture-groups/lecture-group-form/lecture-group-form.component';

interface QuickStartOption {
  label: string;
  value: string;
  description: string;
}

/**
 * The "Szybkie skróty" sidebar shortcuts: pick a student (exam or lectures) or a lecture group
 * (lectures) and jump straight to the station selection dialog.
 */
@Component({
  selector: 'app-quick-start',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, DropdownModule],
  templateUrl: './quick-start.component.html',
  styleUrl: './quick-start.component.scss',
})
export class QuickStartComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly primengDialogService = inject(DialogService);
  private readonly messageService = inject(MessageService);
  private readonly myOskStudentsService = inject(MyOskStudentsService);
  private readonly myOskLectureGroupsService = inject(MyOskLectureGroupsService);

  readonly mode: InternalExamSessionMode =
    this.route.snapshot.data['quickStart'] === 'lectures' ? 'lectures' : 'exam';
  readonly title = this.mode === 'lectures' ? 'Rozpocznij wykłady' : 'Rozpocznij egzamin wewnętrzny';

  /** Lectures can be held for a whole group or for one student; exams are always per student. */
  targetType: 'group' | 'student' = this.mode === 'lectures' ? 'group' : 'student';
  groupOptions: QuickStartOption[] = [];
  studentOptions: QuickStartOption[] = [];
  selectedGroupGuid: string | null = null;
  selectedStudentGuid: string | null = null;
  loading = true;

  private readonly subscriptions = new Subscription();
  private ref: DynamicDialogRef | undefined;

  ngOnInit(): void {
    this.subscriptions.add(
      forkJoin({
        students: this.myOskStudentsService.get(false, '', 'created', 1, LECTURE_GROUP_STUDENTS_PAGE_SIZE),
        groups:
          this.mode === 'lectures'
            ? this.myOskLectureGroupsService.get(false, '', 'created', 1, LECTURE_GROUP_STUDENTS_PAGE_SIZE)
            : of(null),
      })
        .pipe(finalize(() => (this.loading = false)))
        .subscribe({
          next: ({ students, groups }) => {
            this.studentOptions = (students.data ?? []).map((student) => ({
              label: student.email,
              value: student.guid,
              description: (student.displayName ?? '').trim(),
            }));
            this.groupOptions = (groups?.data ?? []).map((group) => ({
              label: group.name,
              value: group.guid,
              description: `kategoria ${group.examName} · kursantów: ${group.studentsCount}`,
            }));
          },
          error: () => this.showLoadError(),
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.ref?.close();
  }

  get canContinue(): boolean {
    return this.targetType === 'group' ? !!this.selectedGroupGuid : !!this.selectedStudentGuid;
  }

  continue(): void {
    if (!this.canContinue) {
      return;
    }
    const data =
      this.targetType === 'group'
        ? (() => {
            const group = this.groupOptions.find((option) => option.value === this.selectedGroupGuid)!;
            return {
              lectureGroupGuid: group.value,
              lectureGroupName: group.label,
              studentDisplayName: group.label,
              mode: 'lectures',
            };
          })()
        : (() => {
            const student = this.studentOptions.find((option) => option.value === this.selectedStudentGuid)!;
            return { studentGuid: student.value, studentDisplayName: student.label, mode: this.mode };
          })();

    this.ref = this.primengDialogService.open(InternalExamDialogComponent, {
      ...centerDynamicModalConfig,
      width: '980px',
      showHeader: true,
      header: this.title,
      data,
    });
  }

  private showLoadError(): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Błąd',
      detail: 'Nie udało się pobrać listy. Odśwież stronę i spróbuj ponownie.',
    });
  }
}
